import { monthNames } from "../../../../../utils/constants";
import { KIND_PRIORITY, isKind } from "./KindTag";

/*
 * Adapter between `GET /institutions/merge/:id/review` and the split view.
 * EVERY server field name the front reads is listed in `F` below — if the server names a field
 * differently, change it here and nowhere else. The components only see the normalised shape:
 *
 *   { summary: { sentence, counts: {kind: n}, perSection: [{ section, label, total, changed, kinds }], residual },
 *     section, total, page, limit,
 *     items: [{ key, model, id, title, subtitle, action: {code,label,detail}, kind, isTarget,
 *               before: { exists }, after: { exists },
 *               lines: [{ path, label, before: Value|null, after: Value|null, changed, kind }],
 *               mergedInto: { name, id }|null }] }
 *   Value = { raw, ref: {id,name,isNew}|null, refs: [{id,name,isNew}]|null, provenance }
 */
const F = {
  summary: "summary",
  sentence: "sentence",
  counts: "counts",
  perSection: "perSection",
  residual: "residualOnSources",
  sectionKey: "section",
  sectionLabel: "label",
  sectionTotal: "total",
  sectionChanged: "changed",
  sectionKinds: "kinds",
  section: "section",
  total: "total",
  page: "page",
  limit: "limit",
  items: "items",
  key: "key",
  model: "model",
  documentId: "documentId",
  title: "title",
  subtitle: "subtitle",
  action: "action",
  actionCode: "code",
  actionLabel: "label",
  actionDetail: "detail",
  isTarget: "isTarget",
  isResult: "isResult",
  facts: "facts",
  factKey: "key",
  factN: "n",
  target: "target",
  before: "before",
  after: "after",
  exists: "exists",
  fields: "fields",
  changedPaths: "changedPaths",
  path: "path",
  label: "label",
  value: "value",
  ref: "ref",
  refs: "refs",
  beforeRef: "beforeRef",
  afterRef: "afterRef",
  beforeRefs: "beforeRefs",
  afterRefs: "afterRefs",
  kind: "kind",
  provenance: "provenance",
  refId: "_id",
  refName: "name",
  refIsNew: "isNew",
};

/** Query parameters of the endpoint. */
export const reviewParams = ({ section, q, changedOnly, page, limit }) => {
  const p = { section };
  if (q) p.q = q;
  p.changedOnly = changedOnly ? 1 : 0;
  if (page) p.page = page;
  if (limit) p.limit = limit;
  return p;
};

/** Server kind codes (and a few tolerated spellings) → front kinds. */
const KIND_ALIASES = {
  id_replaced: "id_replaced",
  idReplaced: "id_replaced",
  id: "id_replaced",
  modified: "modified",
  edited: "modified",
  merged: "merged",
  deleted: "deleted",
  created: "created",
  archived: "archived",
  same: "same",
  identical: "same",
  unchanged: "same",
};
const kindOf = k => (k && KIND_ALIASES[k]) || null;

/** Entity-level kind implied by the record's action code (records endpoint codes). */
const ACTION_KIND = {
  repoint: "id_replaced",
  archive: "archived",
  merge_account: "merged",
  keep_account: "merged",
  rename: "modified",
  address_keep: "id_replaced",
  address_merge: "merged",
  address_edit: "modified",
  responsable_keep: "id_replaced",
  responsable_merge: "merged",
  responsable_edit: "modified",
  blacklist_keep: "id_replaced",
  blacklist_drop: "deleted",
  create: "created",
  created: "created",
};

export const RECORD_SECTIONS = [
  "prestations",
  "bills",
  "batches",
  "principals",
];
export const PAGE_LIMIT = 30;

const OID = /^[a-f0-9]{24}$/i;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const MODE_LABEL = { multiple: "groupée", unique: "par prestation" };

const normRef = r => {
  if (r === null || r === undefined) return null;
  if (typeof r === "string")
    return OID.test(r) ? { id: r, name: null, isNew: false } : null;
  if (typeof r !== "object" || Array.isArray(r)) return null;
  const id = r[F.refId] ? String(r[F.refId]) : null;
  const name = r[F.refName] || r.label || null;
  if (!id && !name && !r[F.refIsNew]) return null;
  return { id, name, isNew: !!r[F.refIsNew] || (!id && !!name && !!r.new) };
};
const isRefObject = v =>
  !!v &&
  typeof v === "object" &&
  !Array.isArray(v) &&
  (F.refId in v || F.refIsNew in v) &&
  (F.refName in v || "label" in v);

/** One field of one side → Value. `side` picks beforeRef / afterRef when the plain `ref` is absent. */
const normValue = (field, side) => {
  if (!field) return null;
  const raw = field[F.value];
  let ref = field[F.ref];
  let refs = field[F.refs];
  // the server's `ref` names the referenced MODEL ("Institution", "Address"…); the reference itself is the value
  if (typeof ref === "string" && !OID.test(ref)) ref = undefined;
  if (ref === undefined)
    ref = side === "before" ? field[F.beforeRef] : field[F.afterRef];
  if (refs === undefined)
    refs = side === "before" ? field[F.beforeRefs] : field[F.afterRefs];
  if (Array.isArray(ref)) {
    refs = ref;
    ref = undefined;
  }
  let nRef = normRef(ref);
  let nRefs = Array.isArray(refs) ? refs.map(normRef).filter(Boolean) : null;
  // a value that is itself a resolved reference (or a list of them) is shown as one
  if (!nRef && !nRefs && isRefObject(raw)) nRef = normRef(raw);
  if (
    !nRef &&
    !nRefs &&
    Array.isArray(raw) &&
    raw.length &&
    raw.every(isRefObject)
  )
    nRefs = raw.map(normRef).filter(Boolean);
  return {
    raw,
    ref: nRef,
    refs: nRefs,
    provenance: field[F.provenance] || null,
  };
};

const emptyRaw = v =>
  v === null ||
  v === undefined ||
  v === "" ||
  (Array.isArray(v) && v.length === 0);

/** Comparison key of a Value — refs compare by id (or name when the id is not known yet). */
export const valueKey = v => {
  if (!v) return "";
  if (v.ref) return `ref:${v.ref.isNew ? "new" : v.ref.id || v.ref.name}`;
  if (v.refs)
    return `refs:${v.refs
      .map(r => (r.isNew ? `new:${r.name}` : r.id || r.name))
      .sort()
      .join("|")}`;
  if (emptyRaw(v.raw)) return "";
  return JSON.stringify(v.raw);
};

const pickKind = kinds => KIND_PRIORITY.find(k => kinds.includes(k)) || null;

/** Text of the « fusionnée dans X » target, from a mergedInto field or the action label. */
const mergedIntoOf = (item, lines) => {
  // read the raw after-side field: a record that disappears has no after values in `lines`
  const afterFields = (item[F.after] && item[F.after][F.fields]) || [];
  const rawField = afterFields.find(x =>
    /mergedInto|merged_into/i.test(x[F.path]),
  );
  const line = lines.find(l => /mergedInto|merged_into|into$/i.test(l.path));
  const v =
    (rawField && normValue(rawField, "after")) ||
    (line && (line.after || line.before));
  if (v) {
    if (v.ref && v.ref.name) return { name: v.ref.name, id: v.ref.id };
    if (typeof v.raw === "string" && v.raw) return { name: v.raw, id: null };
    if (v.raw && typeof v.raw === "object" && v.raw.name)
      return { name: v.raw.name, id: v.raw._id ? String(v.raw._id) : null };
  }
  const a = item[F.action] || {};
  for (const s of [a[F.actionDetail], a[F.actionLabel]]) {
    const m = typeof s === "string" && s.match(/dans\s*:?\s*(.+)$/i);
    if (m && m[1] && !/^[….\s]+$/.test(m[1]))
      return { name: m[1].trim(), id: null };
    if (typeof s === "string" && s && s === a[F.actionDetail])
      return { name: s.trim(), id: null };
  }
  return null;
};

export function normalizeItem(raw, index = 0) {
  const b = raw[F.before] || {};
  const a = raw[F.after] || {};
  const beforeExists = b[F.exists] !== false;
  const afterExists = a[F.exists] !== false;
  const bFields = Array.isArray(b[F.fields]) ? b[F.fields] : [];
  const aFields = Array.isArray(a[F.fields]) ? a[F.fields] : [];
  const changedPaths = new Set(
    Array.isArray(raw[F.changedPaths]) ? raw[F.changedPaths] : [],
  );
  const order = [];
  const byPath = new Map();
  const add = (f, side) => {
    const p = f[F.path] || f[F.label];
    if (!byPath.has(p)) {
      byPath.set(p, { path: p, label: f[F.label] || p });
      order.push(p);
    }
    const e = byPath.get(p);
    e[side] = f;
    if (!e.label || e.label === p) e.label = f[F.label] || p;
  };
  bFields.forEach(f => add(f, "before"));
  aFields.forEach(f => add(f, "after"));

  const lines = order.map(p => {
    const e = byPath.get(p);
    const before = beforeExists ? normValue(e.before, "before") : null;
    const after = afterExists ? normValue(e.after, "after") : null;
    let kind = kindOf(e.after && e.after[F.kind]);
    if (kind === "same") kind = null;
    let changed =
      changedPaths.has(p) ||
      !!kind ||
      (beforeExists && afterExists && valueKey(before) !== valueKey(after));
    if (!beforeExists && afterExists) {
      kind = kind || "created";
      changed = true;
    }
    if (beforeExists && !afterExists) {
      kind = kind || "deleted";
      changed = true;
    }
    if (changed && !kind)
      kind =
        (before && (before.ref || before.refs)) ||
        (after && (after.ref || after.refs))
          ? "id_replaced"
          : "modified";
    return { path: p, label: e.label, before, after, changed, kind };
  });

  const action = raw[F.action]
    ? {
        code: raw[F.action][F.actionCode] || null,
        label: raw[F.action][F.actionLabel] || null,
        detail: raw[F.action][F.actionDetail] || null,
      }
    : null;
  const lineKinds = lines.filter(l => l.changed).map(l => l.kind);
  // the server ranks the kinds of a record itself; without it, derive from the action and the lines
  let kind = kindOf(raw[F.kind]);
  if (!isKind(kind)) {
    kind = action && ACTION_KIND[action.code];
    if (!beforeExists && afterExists) kind = "created";
    else if (beforeExists && !afterExists)
      kind = kind === "merged" ? "merged" : "deleted";
    if (!kind) kind = pickKind(lineKinds);
    if (!isKind(kind)) kind = "same";
  }

  const id = raw[F.documentId] ? String(raw[F.documentId]) : null;
  return {
    key: raw[F.key] || `${raw[F.model] || "item"}-${id || index}`,
    model: raw[F.model] || null,
    id,
    title: raw[F.title] || "(sans titre)",
    subtitle: raw[F.subtitle] || null,
    action,
    kind: kind || "same",
    isTarget: !!(raw[F.isTarget] || raw[F.isResult]),
    before: { exists: beforeExists },
    after: { exists: afterExists },
    lines,
    mergedInto: afterExists ? null : mergedIntoOf(raw, lines),
  };
}

export function normalizeSummary(s) {
  if (!s || typeof s !== "object") return null;
  const per = Array.isArray(s[F.perSection]) ? s[F.perSection] : [];
  return {
    sentence: s[F.sentence] || null,
    counts: s[F.counts] || {},
    residual: typeof s[F.residual] === "number" ? s[F.residual] : null,
    // the tiles' numbers as the server counted them (same facts as its sentence): { key: n }
    facts: Array.isArray(s[F.facts])
      ? Object.fromEntries(
          s[F.facts].map(x => [x[F.factKey], Number(x[F.factN]) || 0]),
        )
      : null,
    target: normRef(s[F.target]),
    perSection: per.map(x => ({
      section: x[F.sectionKey],
      label: x[F.sectionLabel] || null,
      total: Number(x[F.sectionTotal]) || 0,
      changed: Number(x[F.sectionChanged]) || 0,
      kinds: (Array.isArray(x[F.sectionKinds]) ? x[F.sectionKinds] : [])
        .map(kindOf)
        .filter(isKind),
    })),
  };
}

/** The whole response → normalised page. */
export function normalizeReview(raw) {
  const r = raw || {};
  const items = Array.isArray(r[F.items]) ? r[F.items] : [];
  return {
    summary: normalizeSummary(r[F.summary]),
    section: r[F.section] || null,
    total: Number(r[F.total]) || items.length,
    page: Number(r[F.page]) || 1,
    limit: Number(r[F.limit]) || items.length,
    items: items.map(normalizeItem),
  };
}

/* ------------------------------------------------------------------ display */

const pad = n => String(n).padStart(2, "0");
const fmtDateText = s => {
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  const day = `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
  return d.getHours() || d.getMinutes()
    ? `${day} ${pad(d.getHours())}:${pad(d.getMinutes())}`
    : day;
};

/** A raw value as plain words: « vide », oui / non, 12.03.2024, « juin 2025 — groupée »… (`null` = empty). */
export const valueText = v => {
  if (emptyRaw(v)) return null;
  if (typeof v === "boolean") return v ? "oui" : "non";
  // an integer here is a reference, an invoice number, a year, a month or a count — never « 990 004 » or « 2 025 »;
  // only a decimal (an amount) gets the locale format
  if (typeof v === "number")
    return Number.isInteger(v) ? String(v) : v.toLocaleString("fr-CH");
  if (typeof v === "string") {
    if (ISO_DATE.test(v)) return fmtDateText(v);
    return v;
  }
  if (Array.isArray(v))
    return v
      .map(valueText)
      .filter(x => x !== null)
      .join(", ");
  if (typeof v === "object") {
    if ("cloturePrestation" in v)
      return `avertir à la clôture : ${v.cloturePrestation ? "oui" : "non"}`;
    if ("year" in v && "month" in v)
      return `${monthNames[v.month - 1] || v.month} ${v.year} — ${MODE_LABEL[
        v.type
      ] ||
        v.type ||
        "sans règle"}`;
    if (v.name || v.label) return v.name || v.label;
    return JSON.stringify(v);
  }
  return String(v);
};

/** Items of a list value (for per-element diffs), each with a stable comparison key. */
export const listItems = v => {
  if (!v) return null;
  if (v.refs)
    return v.refs.map(r => ({
      key: r.isNew ? `new:${r.name}` : r.id || r.name,
      ref: r,
    }));
  if (Array.isArray(v.raw) && v.raw.length > 1)
    return v.raw.map(x => ({ key: JSON.stringify(x), text: valueText(x) }));
  return null;
};

export const shortId = id => (id ? `…${String(id).slice(-6)}` : null);

/** Singular / plural helper: plural(2, "fiche") → « 2 fiches ». */
export const plural = (n, one, many) =>
  `${Number(n).toLocaleString("fr-CH")} ${n > 1 ? many || `${one}s` : one}`;
